<script setup lang="ts">
import type { CustomerReferenceTag } from '~/types'

interface HybridScalePrice {
  minQuantity: number
  price: number | { amount: number, currency: string }
}

interface Props {
  scalePrices?: HybridScalePrice[]
  campaign?: Record<string, unknown>
  availabilityStatus: string
  customerReference: Array<CustomerReferenceTag>
  humanMedicineRestricted?: boolean
  selectedQuantity?: number
  maxQuantity?: number
}

const props = defineProps<Props>()
const emits = defineEmits<{
  scalePricesHandler: [event: Event]
  selectScaleQuantity: [quantity: number]
}>()

const { hasCustomerReference } = useCustomerReferences()

const sortedScales = computed<HybridScalePrice[]>(() => {
  return props.scalePrices
    ? [...props.scalePrices]
        .filter(scale => !props.maxQuantity || scale.minQuantity <= props.maxQuantity)
        .sort((a, b) => a.minQuantity - b.minQuantity)
    : []
})

const hasScalePrices = computed(() => sortedScales.value.length > 0)

function scalePricesHandler(event: Event): void {
  emits('scalePricesHandler', event)
}

function getScaleBtnVariant(quantity: number) {
  if (!props.selectedQuantity || !sortedScales.value.length) {
    return 'text'
  }
  const applicableScale = sortedScales.value.findLast(scale => props.selectedQuantity! >= scale.minQuantity)

  return applicableScale && quantity === applicableScale.minQuantity ? 'tonal' : 'text'
}
</script>

<template>
  <v-col
    v-if="!props.humanMedicineRestricted && hasScalePrices && !props.campaign && props.availabilityStatus !== 'NOT_AVAILABLE'"
    cols="12"
    lg="6"
    md="12"
    sm="6"
    class="d-flex flex-column justify-start align-lg-end align-md-start align-sm-end mt-0 mt-md-3"
  >
    <v-btn
      :disabled="!hasCustomerReference(props.customerReference)"
      variant="text"
      class="text-vm-grey-7 d-flex justify-end mb-1 px-4 px-md-1 px-lg-4"
      density="compact"
      data-test-id="scale-prices-button"
      data-track-id="product_open_scale_price"
      @click="scalePricesHandler"
    >
      <span class="hide-scale-title">
        {{ $t('scaleDiscount.headline') }}
      </span>
      <template #append>
        <v-icon color="secondary" icon="veticon-tag" />
      </template>
    </v-btn>

    <section
      v-if="sortedScales.length > 0"
      class="d-flex ga-1 mr-lg-2 mr-md-0 mr-sm-2"
      data-test-id="scale-prices-items"
    >
      <v-btn
        v-for="scale in sortedScales.filter(s => s.minQuantity !== 1).slice(0, 4)"
        :key="scale.minQuantity"
        :disabled="!hasCustomerReference(props.customerReference)"
        size="24"
        class="text-body-2 text-vm-grey-9"
        :variant="getScaleBtnVariant(scale.minQuantity)"
        data-track-id="product_select_scale_quantity"
        @click="emits('selectScaleQuantity', scale.minQuantity)"
      >
        {{ scale.minQuantity }}
      </v-btn>
    </section>
  </v-col>
</template>

<style scoped>
@media (min-width: 1280px) and (max-width: 1500px) {
  .hide-scale-title {
    display: none !important;
  }
}
</style>
